import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import {
  setShowHoldAndWinIntro,
  setSceneTransitionActive,
  setUserInterfaceDisabled,
} from "../../../State/actions";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import StorageManager from "../../../utils/StorageManager";

/**
 * HoldAndWinIntroController - Handles hold and win intro animation sequence
 * Requires manual user interaction to close (button click or overlay click)
 */
class HoldAndWinIntroController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    buttonRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.buttonRef = buttonRef;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.introSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state (matching outro pattern)
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;

    // Button attached to spine bone
    this.buttonContainer = null;
    this.buttonText = null;
    this.boneUpdateTicker = null;

    // Text containers attached to spine bones
    this.congratulationsContainer = null;
    this.congratulationsText = null;
    this.youWonContainer = null;
    this.youWonText = null;

    // Internal caches for performance optimization
    this._deviceScaleMultiplierCache = null;
    this._fontSizeMultiplierCache = null;
    this._buttonYOffsetCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeIntroSpine();
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.introSpine || !this.dimensions) return;

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device/orientation-specific multipliers
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    scale *= deviceMultiplier;

    this.introSpine.scale.set(scale);
    // Position at dead center (parent container is already centered)
    this.introSpine.x = 0;
    this.introSpine.y = 0;
  }

  /**
   * Get device-specific scale multiplier for spine animations
   * Cached for performance - invalidated when device/orientation changes
   */
  getDeviceScaleMultiplier() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._deviceScaleMultiplierCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._deviceScaleMultiplierCache;
    }

    // All devices use the same multiplier (1.0)
    const multiplier = 1.0;

    // Cache result
    this._deviceScaleMultiplierCache = multiplier;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multiplier;
  }

  /**
   * Get device-specific font size multiplier for button text
   * Cached for performance - invalidated when device/orientation changes
   */
  getFontSizeMultiplier() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._fontSizeMultiplierCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._fontSizeMultiplierCache;
    }

    // All devices use the same multiplier (1.0)
    const multiplier = 1.0;

    // Cache result
    this._fontSizeMultiplierCache = multiplier;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multiplier;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   * OPTIMIZED: Only recalculate when values actually change
   */
  updateDimensions(dimensionsContext, symbolHeight) {
    // Check if any values actually changed to avoid unnecessary recalculations
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed - this prevents unnecessary updates during animation start
    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions; // Update the extracted dimensions object
    this.symbolHeight = symbolHeight;

    // Invalidate caches when device/orientation changes
    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._deviceScaleMultiplierCache = null;
      this._fontSizeMultiplierCache = null;
      this._buttonYOffsetCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    // Update button text if device changed (tap vs click)
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }

    // CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
    // This ensures the spine is always positioned correctly, preventing jumps on first appearance
    if (
      this.introSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      // Ensure we have valid symbolHeight before positioning
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  /**
   * Initialize the hold and win intro spine
   */
  initializeIntroSpine() {
    try {
      this.introSpine = this.createIntroSpine();

      if (!this.introSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.introSpine.skeleton) missingComponents.push("skeleton");
      if (!this.introSpine.state) missingComponents.push("state");
      if (!this.introSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.introSpine);
      this.introSpine.alpha = 0.001;
      this.introSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.introSpine.parent !== this.container) {
        return;
      }

      this.initialized = true;

      // Invalidate animations cache when spine is reinitialized
      this._availableAnimationsCache = null;

      this.warmUpIntroSpine();

      this.initializeButton();
      this.initializeCongratulationsText();
      this.initializeYouWonText();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Create intro spine from loaded assets
   */
  createIntroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "hns_intro-skeleton";
      const atlasAlias = "hns_intro-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const introSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!introSpine?.skeleton?.data?.animations) {
        return null;
      }

      return introSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   */
  warmUpIntroSpine() {
    if (!this.introSpine?.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.introSpine.state.setAnimation(0, animations[0], false);
        this.introSpine.state.update(0.016);
        this.introSpine.skeleton.updateWorldTransform();
      }
      this.introSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  /**
   * Get available animations from the spine
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.introSpine?.skeleton?.data?.animations) return [];

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.introSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Initialize button and attach it to spine's "tap_to_contiune_text" bone
   *
   * The button is a child of the spine and follows the bone's transform.
   * Position, rotation, and scale are controlled by the bone animation.
   */
  initializeButton() {
    if (!this.introSpine || !this.introSpine.skeleton) {
      return;
    }

    const buttonBone = this.introSpine.skeleton.findBone(
      "tap_to_contiune_text",
    );
    if (!buttonBone) {
      return;
    }

    // Create button container as child of spine
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "intro_continue_button_attached_to_bone";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001; // Render above other elements

    // Get translated button text based on device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Create text style (matching WinAnimationController exactly)
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: 70, // Will be scaled by bone
      fontWeight: "700",
      fill: "#ffffff",
      dropShadow: true,
      dropShadowColor: "#000000",
      dropShadowDistance: 4,
      dropShadowBlur: 4,
      dropShadowAlpha: 0.8,
      align: "center",
    });

    // Create PIXI.Text
    this.buttonText = new PIXI.Text(buttonText, textStyle);
    this.buttonText.anchor.set(0.5);
    this.buttonText.x = 0;
    this.buttonText.y = 0;

    // Scale down the button (0.7x smaller)
    this.buttonText.scale.set(0.7);

    this.buttonContainer.addChild(this.buttonText);
    this.introSpine.addChild(this.buttonContainer);
    this.introSpine.sortableChildren = true;
  }

  /**
   * Initialize congratulations text and attach it to spine's "text_congratulations" bone
   */
  initializeCongratulationsText() {
    if (!this.introSpine || !this.introSpine.skeleton || !this.t) {
      return;
    }

    const congratulationsBone = this.introSpine.skeleton.findBone(
      "text_congratulations",
    );
    if (!congratulationsBone) {
      return;
    }

    // Create container as child of spine
    this.congratulationsContainer = new PIXI.Container();
    this.congratulationsContainer.name =
      "congratulations_text_attached_to_bone";
    this.congratulationsContainer.alpha = 1;
    this.congratulationsContainer.zIndex = 1002;

    // Get translated text
    const congratulationsText = this.t("congratulations").toUpperCase();

    // Based on reference image, this text appears large and bold
    const baseFontSize = 80;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Create text style - gradient with outline and sharp shadow (using Troika font)
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Troika, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "normal",
      fill: ["#fae05c", "#d9a015"], // Gradient: top to bottom
      fillGradientType: PIXI.TEXT_GRADIENT.LINEAR_VERTICAL,
      stroke: "#fff7d0", // Lighter outline
      strokeThickness: 3,
      dropShadow: true,
      dropShadowColor: "#a92400", // Sharp bottom shadow
      dropShadowDistance: 5,
      dropShadowAngle: Math.PI / 2,
      dropShadowBlur: 0, // Sharp (no blur)
      dropShadowAlpha: 1,
      align: "center",
    });

    this.congratulationsText = new PIXI.Text(congratulationsText, textStyle);
    this.congratulationsText.anchor.set(0.5);
    this.congratulationsText.x = 0;
    this.congratulationsText.y = 0;

    this.congratulationsContainer.addChild(this.congratulationsText);
    this.introSpine.addChild(this.congratulationsContainer);
    this.introSpine.sortableChildren = true;
  }

  /**
   * Initialize you won text and attach it to spine's "text_you_won" bone
   */
  initializeYouWonText() {
    if (!this.introSpine || !this.introSpine.skeleton || !this.t) {
      return;
    }

    const youWonBone = this.introSpine.skeleton.findBone("text_you_won");
    if (!youWonBone) {
      return;
    }

    // Create container as child of spine
    this.youWonContainer = new PIXI.Container();
    this.youWonContainer.name = "you_won_text_attached_to_bone";
    this.youWonContainer.alpha = 1;
    this.youWonContainer.zIndex = 1003;

    // Get translated text
    const youWonText = this.t("you_won").toUpperCase();

    // Based on reference image, this text appears slightly smaller than congratulations
    const baseFontSize = 70;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Create text style - gradient with outline and sharp shadow (using Troika font)
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Troika, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "normal",
      fill: ["#fff4be", "#ffdf46"], // Gradient: lighter on top, darker on bottom
      fillGradientType: PIXI.TEXT_GRADIENT.LINEAR_VERTICAL,
      stroke: "#fff7d0", // Lighter outline
      strokeThickness: 3,
      dropShadow: true,
      dropShadowColor: "#a92400", // Sharp bottom shadow
      dropShadowDistance: 5,
      dropShadowAngle: Math.PI / 2,
      dropShadowBlur: 0, // Sharp (no blur)
      dropShadowAlpha: 1,
      align: "center",
    });

    this.youWonText = new PIXI.Text(youWonText, textStyle);
    this.youWonText.anchor.set(0.5);
    this.youWonText.x = 0;
    this.youWonText.y = 0;

    this.youWonContainer.addChild(this.youWonText);
    this.introSpine.addChild(this.youWonContainer);
    this.introSpine.sortableChildren = true;
  }

  /**
   * Get device-specific Y offset for button positioning
   * Adjusts button position based on device and orientation
   * Matches WinAnimationController button offset values
   * Cached for performance - invalidated when device/orientation changes
   */
  getButtonYOffset() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._buttonYOffsetCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._buttonYOffsetCache;
    }

    let offset;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        offset = 335; // Mobile portrait offset
      } else {
        offset = 120; // Mobile landscape offset
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        offset = 290; // Tablet portrait offset
      } else {
        offset = 185; // Tablet landscape offset
      }
    } else {
      offset = 80; // Desktop offset
    }

    // Cache result
    this._buttonYOffsetCache = offset;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return offset;
  }

  /**
   * Sync button transform with spine's "tap_to_contiune_text" bone
   * Called every frame while spine is animating
   * Applies device-specific Y offset for responsive positioning
   */
  updateButtonFromBone() {
    if (
      !this.introSpine ||
      !this.buttonContainer ||
      !this.introSpine.skeleton
    ) {
      return;
    }

    const buttonBone = this.introSpine.skeleton.findBone(
      "tap_to_contiune_text",
    );
    if (!buttonBone) {
      return;
    }

    // Get device-specific Y offset
    const yOffset = this.getButtonYOffset();

    // Apply bone's world transform to button with device-specific Y offset
    this.buttonContainer.x = buttonBone.worldX;
    this.buttonContainer.y = buttonBone.worldY + yOffset;
    this.buttonContainer.rotation =
      (buttonBone.getWorldRotationX() * Math.PI) / 180;
    this.buttonContainer.scale.set(
      buttonBone.getWorldScaleX(),
      buttonBone.getWorldScaleY(),
    );
  }

  /**
   * Update congratulations text position from bone
   */
  updateCongratulationsFromBone() {
    if (
      !this.introSpine ||
      !this.congratulationsContainer ||
      !this.introSpine.skeleton
    ) {
      return;
    }

    const congratulationsBone = this.introSpine.skeleton.findBone(
      "text_congratulations",
    );
    if (!congratulationsBone) {
      return;
    }

    this.congratulationsContainer.x = Math.round(congratulationsBone.worldX);
    this.congratulationsContainer.y = Math.round(congratulationsBone.worldY);
    this.congratulationsContainer.rotation =
      (congratulationsBone.getWorldRotationX() * Math.PI) / 180;
    this.congratulationsContainer.scale.set(
      congratulationsBone.getWorldScaleX(),
      congratulationsBone.getWorldScaleY(),
    );
  }

  /**
   * Update you won text position from bone
   */
  updateYouWonFromBone() {
    if (
      !this.introSpine ||
      !this.youWonContainer ||
      !this.introSpine.skeleton
    ) {
      return;
    }

    const youWonBone = this.introSpine.skeleton.findBone("text_you_won");
    if (!youWonBone) {
      return;
    }

    this.youWonContainer.x = Math.round(youWonBone.worldX);
    this.youWonContainer.y = Math.round(youWonBone.worldY);
    this.youWonContainer.rotation =
      (youWonBone.getWorldRotationX() * Math.PI) / 180;
    this.youWonContainer.scale.set(
      youWonBone.getWorldScaleX(),
      youWonBone.getWorldScaleY(),
    );
  }

  /**
   * Start syncing button with spine bone every frame
   * Uses PIXI.Ticker to update button transforms from bones
   */
  startBoneUpdateLoop() {
    if (!this.introSpine || !this.buttonContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.isPlaying) {
          this.updateButtonFromBone();
          this.updateCongratulationsFromBone();
          this.updateYouWonFromBone();
        }
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing button with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Update button text based on current device (tap vs click)
   */
  updateButtonText() {
    if (!this.buttonText) return;

    // Get translated button text based on current device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Update text content
    this.buttonText.text = buttonText;

    // Recalculate font size (always 70, matching initialization)
    this.buttonText.style.fontSize = 70;
  }

  /**
   * Show continue button with fade-in animation
   */
  showButton() {
    // Show new PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep old React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Hide continue button with fade-out animation
   */
  hideButton() {
    // Hide new PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep old React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Restart the sequence from scratch - used when tab becomes active again after being hidden.
   * The PixiJS ticker stops when the tab is hidden, which freezes the spine animation mid-start.
   * This resets all state so startSequence() can run cleanly again.
   */
  restartSequence() {
    // Only restart if we're in a broken state: intro should be showing but spine isn't playing properly
    // If already waiting for click (sequence completed normally), don't interrupt
    if (this.isWaitingForClick) {
      return;
    }

    // If not playing at all, nothing to restart
    if (!this.isPlaying) {
      return;
    }

    // Clear all pending timeouts from the interrupted sequence
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Stop bone update loop from interrupted sequence
    this.stopBoneUpdateLoop();

    // Reset isPlaying so startSequence() guard doesn't block
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;

    // Restart cleanly
    this.startSequence();
  }

  /**
   * Start the intro animation sequence
   */
  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.introSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.overlayRef.current?.fadeInBackground();

      // Reset user interaction guards (matching outro pattern)
      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      // Clear any existing timeouts
      if (this.skipProtectionTimeout) {
        clearTimeout(this.skipProtectionTimeout);
        this.skipProtectionTimeout = null;
      }
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      // Play hold_and_win_in sound when animation appears
      soundManager.play(SOUND_NAME.hold_and_win_in);

      // Start Hold and Win background music (looping)
      soundManager.play(SOUND_NAME.hold_and_win_music);

      // Spine is already positioned correctly at all times (via updateDimensions)
      // Just ensure final positioning check before showing (spine is always warmed up and positioned)
      if (
        this.introSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      this.introSpine.alpha = 1;
      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
      this.introSpine.state.apply(this.introSpine.skeleton);
      this.introSpine.skeleton.setToSetupPose();

      // Update button and text positions immediately before showing to prevent flash
      this.updateButtonFromBone();
      this.updateCongratulationsFromBone();
      this.updateYouWonFromBone();

      // Start bone update loop to follow spine animation
      this.startBoneUpdateLoop();

      // SKIP PROTECTION: Block skipping for the first second (matching outro pattern)
      // Show button after 1 second
      this.skipProtectionTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();
          this.isWaitingForClick = true;
        }
      }, 1000);

      // Play IN animation, then loop IDLE animation
      this.introSpine.state.setAnimation(0, "in", false);

      // Get IN animation duration
      const inDuration = this.getSpineAnimationDuration("in");

      // After IN completes, start looping IDLE
      const inTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.introSpine.state.setAnimation(0, "idle", true);
          resolve(); // Sequence ready, waiting for user click
        }
      }, inDuration);

      // Store timeout for cleanup
      this.currentTimeout = inTimeout;
    });
  }

  /**
   * Get animation duration from spine data
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.introSpine ||
      !this.introSpine.skeleton ||
      !this.introSpine.skeleton.data ||
      !this.introSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      const animation = this.introSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Handle user click to close animation
   * Optimized debouncing - only 1 click processed, subsequent clicks blocked without creating timeouts
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.introSpine?.state) {
      return false;
    }

    // CRITICAL: If debounce timeout exists, block all clicks (prevents timeout buildup)
    // This ensures only ONE click is processed, all others are ignored
    if (this.debounceTimeout) {
      return false;
    }

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // Check if waiting for click
    if (!this.isWaitingForClick) {
      return false;
    }

    // FIRST CLICK ONLY: Create debounce timeout to block subsequent clicks
    // This timeout will be the ONLY timeout created, no matter how many clicks
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // Close the animation (only happens once due to debounce)
    this.playClosingAnimation();

    return true;
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.introSpine?.state) return;

    this.isWaitingForClick = false;
    this.hideButton();

    // Play hold_and_win_out sound when closing
    soundManager.stop(SOUND_NAME.hold_and_win_in);
    soundManager.play(SOUND_NAME.hold_and_win_out);

    this.introSpine.state.clearListeners();

    this.introSpine.state.setAnimation(0, "out", false);
    this.introSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "out" && this.isPlaying) {
          this.finishAnimation();
        }
      },
    });
  }

  /**
   * Finish the animation and clean up
   * Matches outro's comprehensive cleanup pattern
   */
  finishAnimation() {
    if (!this.introSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.introSpine.alpha = 0.001;

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Stop hold_and_win_in and hold_and_win_out sounds
    soundManager.stop(SOUND_NAME.hold_and_win_in);
    soundManager.stop(SOUND_NAME.hold_and_win_out);
    soundManager.stop(SOUND_NAME.hold_and_win_music);

    // Clear all timeouts (matching outro pattern)
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Reset user interaction guards (matching outro pattern)
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    this.dispatch(setSceneTransitionActive(true));

    // Mark intro as shown in localStorage (user has manually closed it)
    const savedHnwRound = StorageManager.loadHoldAndWinRound();
    if (savedHnwRound) {
      StorageManager.saveHoldAndWinRound({
        ...savedHnwRound,
        introShown: true,
      });
    }

    // Wait for overlay fade out to complete before removing dialog and triggering scene transition
    this.overlayRef.current?.fadeOutBackground(() => {
      if (this.dispatch) {
        this.dispatch(setShowHoldAndWinIntro(false));
        // Enable UI after intro dialog closes (scene transition will handle Hold and Win entry)
        this.dispatch(setUserInterfaceDisabled(false));
      }
    });
  }

  /**
   * Cleanup resources
   * Matches outro's comprehensive cleanup pattern
   */
  cleanup() {
    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Stop Hold and Win music if still playing
    soundManager.stop(SOUND_NAME.hold_and_win_music);

    // Clear all timeouts (matching outro pattern)
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      if (this.buttonText) {
        this.buttonText.destroy();
        this.buttonText = null;
      }
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
    }

    // Cleanup congratulations text
    if (this.congratulationsContainer) {
      this.congratulationsContainer.destroy({ children: true });
      this.congratulationsContainer = null;
      this.congratulationsText = null;
    }

    // Cleanup you won text
    if (this.youWonContainer) {
      this.youWonContainer.destroy({ children: true });
      this.youWonContainer = null;
      this.youWonText = null;
    }

    if (this.introSpine) {
      this.introSpine.state?.clearListeners();
      this.introSpine.state?.clearTracks();
      if (this.introSpine.state) this.introSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.introSpine)) {
        this.container.removeChild(this.introSpine);
      }
      this.introSpine.destroy();
      this.introSpine = null;
    }

    this.initialized = false;
  }
}

export default HoldAndWinIntroController;
